import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import MetaTags from 'components/common/MetaTags';
import MainContent from 'components/NewDesign/MainContent';
import PageHeader from 'components/NewDesign/PageHeader';
import styles from './EventTypes.module.css';
import { getClubEventTypes } from 'api/club/eventTypeApi';
import EventTypeEditPopup from 'components/admin/ClubSettings/EventTypeEditPopup';
import { ReactComponent as SearchIcon } from 'assets/images/icon_search.svg';

const EventTypes = () => {
  const { selectedClub } = useSelector((state) => state.user || {});
  const [eventTypes, setEventTypes] = useState([]);
  const [fetchingEventTypes, setFetchingEventTypes] = useState(true);
  const [selectedEventType, setSelectedEventType] = useState();
  const [showEditPopup, setShowEditPopup] = useState(false);

  // Filter state
  const [searchText, setSearchText] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showHidden, setShowHidden] = useState(true);

  const fetchEventTypes = useCallback(async () => {
    try {
      setFetchingEventTypes(true);
      const response = await getClubEventTypes(selectedClub?.clubID, true);
      if (response.data) {
        setEventTypes(response.data);
      }
    } catch (err) {
      console.log('[EventTypes] Fetch event types error: ', err);
    } finally {
      setFetchingEventTypes(false);
    }
  }, [selectedClub?.clubID]);

  useEffect(() => {
    fetchEventTypes();
  }, [fetchEventTypes]);

  // Get unique categories for dropdown
  const categories = useMemo(() => {
    const uniqueCategories = [...new Set(eventTypes.map((et) => et.category))];
    return uniqueCategories.filter(Boolean).sort();
  }, [eventTypes]);

  // Filter event types based on search, category, and visibility
  const filteredEventTypes = useMemo(() => {
    return eventTypes.filter((eventType) => {
      // Filter by visibility
      if (!showHidden && !eventType.isActive) {
        return false;
      }

      // Filter by category
      if (categoryFilter && eventType.category !== categoryFilter) {
        return false;
      }

      // Filter by search text
      if (searchText) {
        const search = searchText.toLowerCase();
        return (
          eventType.displayName?.toLowerCase().includes(search) ||
          eventType.category?.toLowerCase().includes(search) ||
          eventType.classification?.toLowerCase().includes(search)
        );
      }

      return true;
    });
  }, [eventTypes, searchText, categoryFilter, showHidden]);

  const handleEditEventType = (eventType) => {
    setShowEditPopup(true);
    setSelectedEventType(eventType);
  };

  const handleEditPopupClose = async (refresh) => {
    setShowEditPopup(false);
    setSelectedEventType(null);
    if (refresh) {
      await fetchEventTypes();
    }
  };

  return (
    <>
      <MetaTags title="SplitFast | Club Event Types" />
      <MainContent loading={fetchingEventTypes}>
        <PageHeader
          title="Event Types"
          subTitle="The track and field event types available for your meet programs"
          primarySubTitle
          regularWeightSubTitle
        />
        <div className={styles.tableCard}>
          {/* Filter Bar */}
          <div className={styles.filterBar}>
            <div className={styles.searchWrapper}>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search event types..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              <SearchIcon className={styles.searchIcon} />
            </div>
            <div className={styles.filterGroup}>
              <label htmlFor="categoryFilter" className={styles.filterLabel}>
                Category:
              </label>
              <select
                id="categoryFilter"
                className={styles.filterSelect}
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="">All</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.filterGroup}>
              <label className={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showHidden}
                  onChange={(e) => setShowHidden(e.target.checked)}
                />
                Show hidden
              </label>
            </div>
          </div>

          {/* Table */}
          <div className={styles.tableWrapper}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  <th>Event Name</th>
                  <th>Category</th>
                  <th>Classification</th>
                  <th>Sort Order</th>
                  <th>Visible</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filteredEventTypes.length === 0 ? (
                  <tr>
                    <td colSpan={6} className={styles.emptyState}>
                      No event types found
                    </td>
                  </tr>
                ) : (
                  filteredEventTypes.map((eventType) => (
                    <tr key={eventType.eventCode}>
                      <td className={styles.eventName}>
                        {eventType.displayName}
                      </td>
                      <td>{eventType.category}</td>
                      <td>{eventType.classification}</td>
                      <td>{eventType.sortOrder}</td>
                      <td>
                        <span
                          className={
                            eventType.isActive
                              ? styles.badgeVisible
                              : styles.badgeHidden
                          }
                        >
                          {eventType.isActive ? 'Visible' : 'Hidden'}
                        </span>
                      </td>
                      <td>
                        <button
                          onClick={() => handleEditEventType(eventType)}
                          className={styles.editButton}
                          title="Edit Event Type"
                        >
                          ✏️
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        <EventTypeEditPopup
          clubID={selectedClub?.clubID}
          isOpen={showEditPopup}
          onClose={(e) => handleEditPopupClose(e)}
          eventType={selectedEventType}
          mode="edit"
        />
      </MainContent>
    </>
  );
};

export default EventTypes;
